import React, { useEffect, useMemo, useState } from 'react'
import { lockBodyScroll } from '@/utils/bodyScrollLock'
import type { ProductDetail } from '@/types'
import productService from '@/services/productService'
import { getDayName } from '@/utils/deliveryDays'

const PRODUCT_IMAGE = '/milk-bottle.jpg'

const formatPrice = (price: number): string => `£${price.toFixed(2)}`

/** "Mondays", "Mondays and Thursdays", "Mondays, Thursdays and Saturdays". */
const listDays = (days: number[]): string => {
  const names = days.map(day => `${getDayName(day)}s`)

  return names.length < 2
    ? (names[0] ?? '')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

interface ProductDetailModalProps {
  /** The CRM product GUID to show; null closes the dialog. */
  productId: string | null
  /** Shown while the detail loads, so the dialog isn't blank on open. */
  fallbackName?: string | null
  /** The customer's own price for this product, which the detail doesn't know. */
  unitPrice?: number | null
  /** Optional call to action, e.g. "Add to my order" from the products page. */
  actionLabel?: string
  onAction?: () => void
  /** Shown in place of the action when the product can't be ordered. */
  actionNote?: string
  /**
   * The days this product can be delivered on, and the days the customer
   * takes. Supplying both adds a Deliveries tab; not every caller knows, and
   * an empty tab is worse than none.
   */
  deliverableDays?: number[]
  customerDeliveryDays?: number[]
  onClose: () => void
}

/**
 * Product detail: the packshot carries the name and price, with the copy
 * (description, allergens, nutrition) tabbed alongside.
 *
 * The dialog is a fixed size and each tab scrolls inside it, so switching tabs
 * doesn't resize the window under the customer's cursor — the sections vary
 * from a line of allergens to a full nutrition table.
 *
 * The HTML fields are sanitised server-side (App\Support\SafeHtml) rather than
 * here, so this can render them directly. Woo copy is first-party but
 * CMS-authored, which is why it is cleaned at all.
 */
const ProductDetailModal: React.FC<ProductDetailModalProps> = ({
  productId,
  fallbackName,
  unitPrice,
  actionLabel,
  onAction,
  actionNote,
  deliverableDays,
  customerDeliveryDays,
  onClose,
}) => {
  const [detail, setDetail] = useState<ProductDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState(0)

  useEffect(() => {
    if (!productId) return

    let cancelled = false
    setLoading(true)
    setError(null)
    setDetail(null)
    setTab(0)

    productService.getProductDetail(productId)
      .then(d => { if (!cancelled) setDetail(d) })
      .catch(err => {
        console.error('Failed to load product detail:', err)
        if (!cancelled) setError('We could not load this product just now.')
      })
      .finally(() => { if (!cancelled) setLoading(false) })

    return () => { cancelled = true }
  }, [productId])

  useEffect(() => {
    const onEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    let releaseScroll: (() => void) | undefined
    if (productId) {
      document.addEventListener('keydown', onEscape)
      releaseScroll = lockBodyScroll()
    }
    return () => {
      document.removeEventListener('keydown', onEscape)
      releaseScroll?.()
    }
  }, [productId, onClose])

  /**
   * Which days this product reaches, said plainly. Only when the caller knows
   * both halves: without the customer's own days, "available on Thursdays"
   * cannot say whether that is all of them or a restriction.
   */
  const deliveryCopy = useMemo<string | null>(() => {
    if (!deliverableDays || !customerDeliveryDays || customerDeliveryDays.length === 0) {
      return null
    }

    const reachable = customerDeliveryDays.filter(day => deliverableDays.includes(day))

    if (reachable.length === 0) {
      return `<p>This product isn't available on your delivery days (${listDays(customerDeliveryDays)}).</p>`
    }

    if (reachable.length === customerDeliveryDays.length) {
      const scope = customerDeliveryDays.length === 2 ? 'both' : 'all'

      return `<p>This product is available on ${scope} your delivery days: ${listDays(reachable)}.</p>`
    }

    return `<p>This product is only available on ${listDays(reachable)}.</p>`
      + `<p>You take deliveries on ${listDays(customerDeliveryDays)}.</p>`
  }, [deliverableDays, customerDeliveryDays])

  /**
   * Only the sections that actually have copy. 44 of 61 products carry
   * allergens and 58 carry nutrition, so a fixed set of tabs would leave empty
   * ones on a good number of products.
   */
  const sections = useMemo(() => {
    if (!detail) return []
    return [
      { key: 'about', label: 'About', html: detail.description },
      { key: 'allergy', label: 'Allergens', html: detail.allergy_info },
      { key: 'nutrition', label: 'Nutrition', html: detail.nutritional_info },
      { key: 'deliveries', label: 'Deliveries', html: deliveryCopy },
    ].filter(s => !!s.html)
  }, [detail, deliveryCopy])

  if (!productId) return null

  const name = detail?.name || fallbackName || 'Product'
  const active = sections[Math.min(tab, sections.length - 1)]

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="modal-container sun-product-modal" role="dialog" aria-modal="true" aria-label={name}>
        <button className="modal-close-floating" onClick={onClose} aria-label="Close">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div
          className="sun-product-media"
          style={{ backgroundImage: `url(${detail?.image_url || PRODUCT_IMAGE})` }}
        >
          <div className="sun-product-media-text">
            <span className="sun-product-media-name">{name}</span>
            {unitPrice != null && (
              <span className="sun-product-media-price">{formatPrice(unitPrice)} each</span>
            )}
          </div>
        </div>

        <div className="sun-product-body">
          {loading ? (
            <p className="sun-empty">Loading...</p>
          ) : error ? (
            <p className="field-error">{error}</p>
          ) : (
            <>
              {sections.length > 1 && (
                <div className="sun-product-tabs" role="tablist">
                  {sections.map((section, i) => (
                    <button
                      key={section.key}
                      type="button"
                      role="tab"
                      aria-selected={i === tab}
                      className={`sun-product-tab ${i === tab ? 'active' : ''}`}
                      onClick={() => setTab(i)}
                    >
                      {section.label}
                    </button>
                  ))}
                </div>
              )}

              {actionLabel && onAction ? (
                <button type="button" className="sun-btn sun-product-action" onClick={onAction}>
                  {actionLabel}
                </button>
              ) : actionNote ? (
                <p className="sun-product-note">{actionNote}</p>
              ) : null}

              <div className="sun-product-scroll">
                {active ? (
                  <div
                    className="sun-product-copy"
                    dangerouslySetInnerHTML={{ __html: active.html as string }}
                  />
                ) : (
                  <p className="sun-empty">
                    We don&rsquo;t have any more detail for this product yet.
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProductDetailModal
